import React from 'react';
import { Mail, Linkedin, Shield, Activity, Heart, Lock, Users, ArrowRight } from 'lucide-react';
export function App() {
  return <div className="min-h-screen w-full flex flex-col items-center" data-id="element-0">
      {/* Navigation / Header - Minimal */}
      <header className="w-full max-w-3xl px-6 py-8 flex justify-between items-center" data-id="element-1">
        <div className="font-semibold text-medical-teal tracking-tight text-lg" data-id="element-2">
          Bly-Baret LLC
        </div>
        {/* Optional: Simple nav or just keep it clean as requested */}
      </header>

      <main className="w-full flex-1 flex flex-col items-center" data-id="element-3">
        {/* 1. Hero Section */}
        <section className="w-full max-w-3xl px-6 py-16 md:py-24 text-center" data-id="element-4">
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-medium leading-tight mb-8 text-medical-heading" data-id="element-5">
            Exploring better ways for people with IBD to live more comfortably
            between clinic visits.
          </h1>
          <p className="text-lg md:text-xl text-medical-text leading-relaxed max-w-2xl mx-auto mb-10" data-id="element-6">
            Bly-Baret LLC is an early-stage product studio focused on building
            thoughtful, patient-centered tools for people with Crohn's and
            Ulcerative Colitis.
          </p>
        </section>

        {/* 2. The Problem */}
        <section className="w-full bg-white py-16 md:py-20 border-y border-stone-100" data-id="element-7">
          <div className="max-w-3xl mx-auto px-6" data-id="element-8">
            <h2 className="text-2xl md:text-3xl mb-10 text-medical-heading" data-id="element-9">
              The Reality of IBD
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12" data-id="element-10">
              <div className="space-y-4" data-id="element-11">
                <div className="flex items-start" data-id="element-12">
                  <Activity className="w-5 h-5 text-medical-teal mt-1 mr-3 flex-shrink-0" data-id="element-13" />
                  <p className="text-medical-text leading-relaxed" data-id="element-14">
                    IBD is managed daily, not just in the clinic. The gap
                    between visits is where life happens.
                  </p>
                </div>
                <div className="flex items-start" data-id="element-15">
                  <Heart className="w-5 h-5 text-medical-teal mt-1 mr-3 flex-shrink-0" data-id="element-16" />
                  <p className="text-medical-text leading-relaxed" data-id="element-17">
                    Patients carry an ongoing mental and physical load that is
                    often invisible to others.
                  </p>
                </div>
              </div>
              <div className="space-y-4" data-id="element-18">
                <div className="flex items-start" data-id="element-19">
                  <Shield className="w-5 h-5 text-medical-teal mt-1 mr-3 flex-shrink-0" data-id="element-20" />
                  <p className="text-medical-text leading-relaxed" data-id="element-21">
                    Symptoms, triggers, and flares are notoriously hard to
                    predict, creating anxiety.
                  </p>
                </div>
                <div className="flex items-start" data-id="element-22">
                  <Lock className="w-5 h-5 text-medical-teal mt-1 mr-3 flex-shrink-0" data-id="element-23" />
                  <p className="text-medical-text leading-relaxed" data-id="element-24">
                    Personal health data is often fragmented, overwhelming, or
                    abandoned entirely.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 3. What We're Building */}
        <section className="w-full max-w-3xl px-6 py-16 md:py-24" data-id="element-25">
          <h2 className="text-2xl md:text-3xl mb-6 text-medical-heading" data-id="element-26">
            What We're Building
          </h2>

          <div className="bg-medical-teal-light rounded-2xl p-8 md:p-10 mb-12" data-id="element-27">
            <ul className="space-y-4" data-id="element-28">
              {['Simple, low-friction daily tracking', 'Pattern discovery over time', 'Better preparation for doctor visits', 'Wearable data integration'].map((item, i) => <li key={i} className="flex items-center text-medical-heading font-medium" data-id="element-29">
                  <span className="w-2 h-2 rounded-full bg-medical-teal mr-4" data-id="element-30"></span>
                  {item}
                </li>)}
            </ul>
          </div>

          <p className="text-medical-text italic border-l-4 border-medical-teal pl-6 py-2 bg-white/50" data-id="element-31">
            "We're exploring and learning how to give patients the support they need. This is a wellness application designed to complement clinical care. It is not diagnostic"
          </p>
        </section>

        {/* 4. Product Walkthrough Video */}
        <section className="w-full bg-white py-16 md:py-24 border-y border-stone-100" data-id="element-32">
          <div className="max-w-3xl mx-auto px-6" data-id="element-33">
            <h2 className="text-2xl md:text-3xl mb-6 text-medical-heading" data-id="element-34">
              V1 Product
            </h2>
            <p className="text-medical-text mb-8 max-w-2xl" data-id="element-35">
              Releasing in early Spring, 2026
            </p>

            <div className="w-full flex justify-center bg-stone-100 rounded-xl p-8 mb-4" data-id="element-36">
              <video autoPlay loop muted controls playsInline className="max-h-[600px] rounded-lg shadow-lg" src="https://cdn.magicpatterns.com/uploads/oJFpQxzABWJWgrGynU2j5D/Illustration.undefined" data-id="element-37">
                Your browser does not support the video tag.
              </video>
            </div>
            <p className="text-sm text-stone-500 text-center" data-id="element-38"></p>
          </div>
        </section>

        {/* 5. Design & Ethics Principles */}
        <section className="w-full max-w-3xl px-6 py-16 md:py-24" data-id="element-39">
          <h2 className="text-2xl md:text-3xl mb-10 text-medical-heading" data-id="element-40">
            Design & Ethics Principles
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-8" data-id="element-41">
            {[{
            title: 'Patient-first design',
            desc: 'Built with the IBD community, by someone who has IBD.'
          }, {
            title: 'Privacy by default',
            desc: 'Data stays on device whenever possible. No selling data.'
          }, {
            title: 'No black boxes',
            desc: 'Transparent, explainable insights. No magic claims.'
          }, {
            title: 'Clinical collaboration',
            desc: 'Designed to support the patient-provider relationship.'
          }].map((principle, i) => <div key={i} className="flex flex-col" data-id="element-42">
                <h3 className="text-lg font-semibold text-medical-heading mb-2" data-id="element-43">
                  {principle.title}
                </h3>
                <p className="text-medical-text text-sm leading-relaxed" data-id="element-44">
                  {principle.desc}
                </p>
              </div>)}
          </div>
        </section>

        {/* 6. Team */}
        <section className="w-full bg-medical-teal text-white py-16 md:py-20" data-id="element-45">
          <div className="max-w-4xl mx-auto px-6" data-id="element-46">
            <h2 className="text-2xl md:text-3xl font-medium mb-12 text-white" data-id="element-47">
              Who We Are
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 mb-10" data-id="element-48">
              {/* Jaclyn Shumate */}
              <div className="flex flex-col" data-id="element-49">
                <h3 className="text-xl font-semibold mb-1" data-id="element-50">Jaclyn Shumate</h3>
                <p className="text-teal-100 text-sm mb-4" data-id="element-51">Founder & Designer</p>
                <p className="text-teal-50 leading-relaxed mb-6 flex-1" data-id="element-52">
                  Jaclyn Shumate is an ex-Microsoft product and design leader
                  with over 15 years of experience building thoughtful,
                  user-centered software across enterprise and AI platforms.
                  Jaclyn founded Bly-Baret LLC to explore more humane,
                  responsible ways technology can support people with IBD to
                  live better, more full lives.
                </p>
                <a href="https://www.linkedin.com/in/jaclynshumate/" target="_blank" rel="noopener noreferrer" className="group flex items-center text-white hover:text-teal-100 transition-colors w-fit" data-id="element-53">
                  <Linkedin className="w-4 h-4 mr-2 group-hover:scale-110 transition-transform" data-id="element-54" />
                  <span className="text-sm" data-id="element-55">LinkedIn</span>
                </a>
              </div>

              {/* Peter Held */}
              <div className="flex flex-col" data-id="element-56">
                <h3 className="text-xl font-semibold mb-1" data-id="element-57">Peter Held</h3>
                <p className="text-teal-100 text-sm mb-4" data-id="element-58">Principal Engineer</p>
                <p className="text-teal-50 leading-relaxed mb-6 flex-1" data-id="element-59">
                  Peter Held is Principal Engineer of Bly-Baret with over 25
                  years of experience. Formerly Director of Engineering at
                  Flipboard, he leads the architecture and development of
                  Bly-Baret's patient-centered IBD companion.
                </p>
                <a href="https://www.linkedin.com/in/petertheld/" target="_blank" rel="noopener noreferrer" className="group flex items-center text-white hover:text-teal-100 transition-colors w-fit" data-id="element-60">
                  <Linkedin className="w-4 h-4 mr-2 group-hover:scale-110 transition-transform" data-id="element-61" />
                  <span className="text-sm" data-id="element-62">LinkedIn</span>
                </a>
              </div>
            </div>

            <div className="flex items-center justify-center text-teal-100 text-sm pt-6 border-t border-teal-700/30" data-id="element-63"></div>
          </div>
        </section>

        {/* 7. Contact & Follow-Up */}
        <section className="w-full max-w-3xl px-6 py-20 flex flex-col items-center text-center" data-id="element-64">
          <h2 className="text-2xl md:text-3xl mb-6 text-medical-heading" data-id="element-65">
            Let's Connect
          </h2>
          <p className="text-lg text-medical-text mb-10 max-w-xl" data-id="element-66">
            We'd love to hear from you.
          </p>

          <div className="flex justify-center items-center w-full" data-id="element-67">
            <a href="mailto:hello@bly-baret.com" className="group flex items-center px-6 py-3 bg-medical-heading text-white rounded-lg hover:bg-stone-800 transition-colors duration-200" data-id="element-68">
              <Mail className="w-5 h-5 mr-2 group-hover:scale-110 transition-transform" data-id="element-69" />
              <span data-id="element-70">hello@bly-baret.com</span>
            </a>
          </div>
        </section>
      </main>

      <footer className="w-full py-8 text-center text-stone-400 text-sm" data-id="element-71">
        <p data-id="element-72">&copy; {new Date().getFullYear()} Bly-Baret. All rights reserved.</p>
      </footer>
    </div>;
}